/**
 * The signed-in wallet's notifications, the newest first: what happened to its requests, its
 * campaigns, its items and its money, and what Morcast announced. Opening the page marks them
 * read. Those that were unread keep their mark while the page stays open.
 */

import { Bell } from "lucide-react";
import { useEffect } from "react";

import { RequireWallet } from "../components/RequireWallet.js";
import { EmptyState, ErrorMessage, Loading, PageHeader, styles } from "../components/ui.js";
import { NotificationItem } from "../notifications/NotificationItem.js";
import {
  PAGE_NOTICES,
  useMarkNotificationsRead,
  useNotifications,
} from "../notifications/notifications.js";

export function Notifications() {
  return (
    <RequireWallet reason="Sign in to see what Morcast has told your wallet.">
      <NotificationsPage />
    </RequireWallet>
  );
}

function NotificationsPage() {
  const list = useNotifications(PAGE_NOTICES);
  const { markRead } = useMarkNotificationsRead();
  const unread = list.data?.unread ?? 0;

  // Whatever is unread once the list has loaded, or arrives while the page is open, is now read.
  useEffect(() => {
    if (unread > 0) {
      markRead();
    }
  }, [unread, markRead]);

  const notices = list.data?.notifications ?? [];
  return (
    <section className={styles.narrowPage}>
      <PageHeader title="Notifications">
        What happened to your requests, your campaigns, your items and your money, and what Morcast
        announced. Notices stay here for 90 days.
      </PageHeader>
      <div className="mt-8">
        {list.isPending && <Loading />}
        {list.isError && <ErrorMessage error={list.error} />}
        {list.data && notices.length === 0 && (
          <EmptyState icon={Bell} title="No notifications yet">
            Morcast tells you here when something happens, such as a decision on your request or an
            item that was submitted.
          </EmptyState>
        )}
        {notices.length > 0 && (
          <ul aria-label="Notifications" className={`${styles.card} space-y-1 !p-2 sm:!p-3`}>
            {notices.map((notice) => (
              <li key={notice.id}>
                <NotificationItem notice={notice} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
